import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SecurityEnhancedAuth } from './SecurityEnhancedAuth';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from "@/lib/utils";
const industryOptions = [
  'Technology & Software',
  'Financial Services',
  'Healthcare & Life Sciences',
  'Professional Services',
  'Manufacturing & Industrial',
  'Real Estate & Construction',
  'Education',
  'Retail & E-commerce',
  'Hospitality & Travel',
  'Public Sector',
  'Charity & Non-Profit',
  'Sales & Revenue Operations',
  'Marketing & Communications',
  'Customer Operations',
  'People Operations',
  'General Business Operations'
];
const signupSchema = z.object({
  email: z.string().email('Please enter a valid email').min(1, 'Email is required'),
  password: z.string().min(8, 'Password must be at least 8 characters').max(128, 'Password is too long').regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number'),
  confirmPassword: z.string(),
  fullName: z.string().min(2, 'Full name is required').max(100, 'Name is too long'),
  company: z.string().min(1, 'Company is required').max(100, 'Company name is too long'),
  industry: z.string().min(1, 'Please select an industry')
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});
export function SignupForm() {
  const [isLoading, setIsLoading] = useState(false);
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const form = useForm<{
    email: string;
    password: string;
    confirmPassword: string;
    fullName: string;
    company: string;
    industry: string;
  }>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      company: '',
      industry: ''
    }
  });
  const onSubmit = async (data: {
    email: string;
    password: string;
    confirmPassword: string;
    fullName: string;
    company: string;
    industry: string;
  }) => {
    setIsLoading(true);
    try {
      const {
        data: authData,
        error
      } = await SecurityEnhancedAuth.signUp(data.email, data.password, {
        full_name: data.fullName,
        company: data.company,
        industry: data.industry
      });
      if (error) {
        let errorMessage = 'Account creation failed. Please try again.';
        if (error.message.includes('User already registered')) {
          errorMessage = 'An account with this email already exists. Please sign in instead.';
        } else if (error.message.includes('Password should be at least')) {
          errorMessage = 'Password is too weak. Please choose a stronger password.';
        } else if (error.message.includes('Unable to validate email address')) {
          errorMessage = 'Please enter a valid email address.';
        } else if (error.message.includes('Signup is disabled')) {
          errorMessage = 'Account creation is currently disabled. Please contact support.';
        }
        toast({
          title: "Sign Up Failed",
          description: errorMessage,
          variant: "destructive"
        });
        return;
      }
      if (authData?.user) {
        toast({
          title: "Account Created!",
          description: "Please check your email to confirm your account before signing in.",
          duration: 6000
        });

        // Reset form after successful signup
        form.reset();
      }
    } catch (error: any) {
      toast({
        title: "Sign Up Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField control={form.control} name="fullName" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your full name" {...field} autoComplete="name" className={cn("transition-all focus-within:ring-2 focus-within:ring-brand-400", isMobile ? "h-12 text-base px-4" : "")} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />
        
        <FormField control={form.control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium">Email</FormLabel>
              <FormControl>
                <Input placeholder="Enter your email" {...field} type="email" autoComplete="email" className={cn("transition-all focus-within:ring-2 focus-within:ring-brand-400", isMobile ? "h-12 text-base px-4" : "")} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        <FormField control={form.control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium">Company</FormLabel>
              <FormControl>
                <Input placeholder="Enter your company name" {...field} autoComplete="organization" className={cn("transition-all focus-within:ring-2 focus-within:ring-brand-400", isMobile ? "h-12 text-base px-4" : "")} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        <FormField control={form.control} name="industry" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium">Industry</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={cn("transition-all focus-within:ring-2 focus-within:ring-brand-400", isMobile ? "h-12 text-base px-4" : "")}>
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {industryOptions.map(industry => <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="text-xs" />
            </FormItem>} />
        
        <FormField control={form.control} name="password" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium">Password</FormLabel>
              <FormControl>
                <Input placeholder="Create a password" {...field} type="password" autoComplete="new-password" className={cn("transition-all focus-within:ring-2 focus-within:ring-brand-400", isMobile ? "h-12 text-base px-4" : "")} />
              </FormControl>
              <FormMessage className="text-xs" />
              <p className="text-xs text-gray-500 mt-1">
                Must be at least 8 characters with uppercase, lowercase, and number
              </p>
            </FormItem>} />
        
        <FormField control={form.control} name="confirmPassword" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium">Confirm Password</FormLabel>
              <FormControl>
                <Input placeholder="Confirm your password" {...field} type="password" autoComplete="new-password" className={cn("transition-all focus-within:ring-2 focus-within:ring-brand-400", isMobile ? "h-12 text-base px-4" : "")} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />
        
        <Button type="submit" size={isMobile ? "lg" : "default"} disabled={isLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0 font-medium shadow-sm">
          {isLoading ? <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating Account...
            </span> : 'Create Account'}
        </Button>
      </form>
    </Form>;
}